import { useEffect, useState } from "react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { LANGS, getLang } from "@/i18n";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ShieldCheck, HardHat } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatApiErrorDetail } from "@/lib/format";
import { EditionCards } from "@/components/EditionCards";
import { InstallAppButton } from "@/components/InstallPrompt";
import { ServerWakingBanner } from "@/components/AppBanners";
import { warmUp } from "@/lib/api";

const HERO = "https://images.unsplash.com/photo-1709934645644-f055bb2b75de?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600";

export default function Login() {
  const { login, logout } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState("admin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    warmUp();
    const notice = sessionStorage.getItem("login-notice");
    if (notice) { toast.error(notice); sessionStorage.removeItem("login-notice"); }
  }, []);

  const submit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      const u = await login(email.trim(), password);
      if (role === "admin" && u.role !== "admin") {
        logout();
        toast.error("Questo account non è un amministratore. Usa la scheda Collaboratore.");
        return;
      }
      toast.success("Accesso effettuato");
      navigate(u.role === "admin" ? "/" : u.role === "junior" ? "/agenda" : "/preventivi");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Accesso non riuscito");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background relative">
      <div className="absolute top-4 right-4 z-10"><LanguageSwitcher /></div>
      {/* Brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 bg-slate-900 text-white relative overflow-hidden">
        <img src={HERO} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-60" data-testid="login-hero-image" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/95 via-slate-950/75 to-slate-900/45" />
        <div className="relative flex items-center gap-3">
          <img src="/k-gestionale.png" alt="K Gestionale" className="h-12 w-12 rounded-xl" />
          <div>
            <div className="font-heading font-extrabold text-lg tracking-tight">K Gestionale</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-primary-foreground/70">Il gestionale per imprese edili</div>
          </div>
        </div>
        <div className="relative space-y-6">
          <h2 className="font-heading text-4xl xl:text-5xl font-extrabold leading-tight tracking-tight drop-shadow-sm" data-testid="login-hero-title">
            Gestisci la tua ditta<br />con un <span className="text-primary">Click</span>
          </h2>
          <p className="text-slate-300 text-base max-w-md leading-relaxed">
            Preventivi, agenda, cantieri, incassi e un assistente IA che ti ascolta anche a voce. Due versioni, scegli quella giusta per la tua ditta:
          </p>
          <div className="max-w-lg" data-testid="login-editions">
            <EditionCards variant="dark" />
          </div>
        </div>
        <div className="relative text-xs text-slate-400" translate="no">{LANGS.find((l) => l.code === getLang())?.label} · € EUR</div>
      </div>

      {/* Form */}
      <div className="flex items-start sm:items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm space-y-7 animate-in-up">
          <div className="lg:hidden relative -mx-6 -mt-6 sm:mx-0 sm:mt-0 sm:rounded-2xl overflow-hidden h-48 text-white" data-testid="login-mobile-hero">
            <img src={HERO} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/60 to-slate-900/30" />
            <div className="relative h-full flex flex-col justify-between p-5">
              <div className="flex items-center gap-2.5">
                <img src="/k-gestionale.png" alt="K Gestionale" className="h-9 w-9 rounded-lg" />
                <span className="font-heading font-extrabold text-lg">K Gestionale</span>
              </div>
              <div className="font-heading text-2xl font-extrabold leading-tight">Gestisci la tua ditta<br />con un <span className="text-primary">Click</span></div>
            </div>
          </div>
          <div className="lg:hidden space-y-2" data-testid="login-mobile-editions">
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Due versioni, scegli la tua</div>
            <EditionCards variant="light" />
          </div>
          <div>
            <h1 className="font-heading text-3xl font-extrabold tracking-tight">Accedi al tuo account</h1>
            <p className="text-muted-foreground text-sm mt-1.5">Seleziona il tipo di accesso e inserisci le credenziali.</p>
          </div>

          <Tabs value={role} onValueChange={setRole}>
            <TabsList className="grid w-full grid-cols-2" data-testid="login-role-tabs">
              <TabsTrigger value="admin" data-testid="login-role-admin" className="gap-1.5"><ShieldCheck className="w-4 h-4" /> Amministratore</TabsTrigger>
              <TabsTrigger value="staff" data-testid="login-role-staff" className="gap-1.5"><HardHat className="w-4 h-4" /> Collaboratore</TabsTrigger>
            </TabsList>
          </Tabs>

          <form onSubmit={submit} className="space-y-4">
            <ServerWakingBanner inline />
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" data-testid="login-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nome@azienda.it" required />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link to="/password-dimenticata" state={{ email }} className="text-xs font-medium text-primary hover:underline" data-testid="login-forgot-link">Password dimenticata?</Link>
              </div>
              <Input id="password" type="password" data-testid="login-password-input" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required />
            </div>
            <Button type="submit" className="w-full h-11" data-testid="login-submit-button" disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Accedi come {role === "admin" ? "Amministratore" : "Collaboratore"}</>}
            </Button>
          </form>

          <div className="rounded-lg border border-border bg-secondary/50 p-4 text-sm flex items-center justify-between gap-3">
            <span className="text-muted-foreground">Nuova impresa? <span className="block text-xs">Prova gratis per 14 giorni</span></span>
            <Link to="/registrati" className="font-semibold text-primary hover:underline" data-testid="login-register-link">Registra la tua azienda →</Link>
          </div>

          <InstallAppButton className="w-full h-11" testid="login-install-app-button" />

          <p className="text-sm text-center text-muted-foreground leading-relaxed" data-testid="login-legal">
            Accedendo accetti i <Link to="/termini" className="text-primary underline inline-block py-2">Termini</Link>,
            l'<Link to="/privacy" className="text-primary underline inline-block py-2">Informativa Privacy</Link> e la <Link to="/cookie" className="text-primary underline inline-block py-2">Cookie Policy</Link>.
          </p>
        </div>
      </div>
    </div>
  );
}
